import type { FC } from 'react'
import { useCallback } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { IconButton, Tooltip, useTheme } from '@mui/material'
import { Check, ContentCopy } from '@mui/icons-material'
import { useCopyToClipboard } from '@/common/hooks'
import { standardTooltipProps } from './tooltipStyles'

type CopyButtonVariant = 'default' | 'small' | 'overlay' | 'controls'

interface CopyButtonProps {
  readonly text: string
  readonly variant?: CopyButtonVariant
  readonly tooltipText?: string
  readonly color?: string
  readonly iconSize?: string | number
  readonly sx?: SxProps<Theme>
}

export const CopyButton: FC<CopyButtonProps> = ({
  text,
  variant = 'default',
  tooltipText = 'Copy',
  color,
  iconSize,
  sx,
}) => {
  const theme = useTheme()
  const { copied, copy } = useCopyToClipboard()

  const handleClick = useCallback(() => {
    void copy(text)
  }, [copy, text])

  const tooltipTitle = copied ? 'Copied!' : tooltipText
  const buttonSx = resolveButtonSx(variant, copied, theme, color, sx)
  const iconSx = resolveIconSx(variant, iconSize)

  return (
    <Tooltip {...standardTooltipProps} title={tooltipTitle}>
      <IconButton onClick={handleClick} size="small" sx={buttonSx}>
        {copied ? <Check sx={iconSx} /> : <ContentCopy sx={iconSx} />}
      </IconButton>
    </Tooltip>
  )
}

function resolveButtonSx(
  variant: CopyButtonVariant,
  copied: boolean,
  theme: Theme,
  customColor?: string,
  customSx?: SxProps<Theme>,
): SxProps<Theme> {
  const baseStyles = variantButtonStyles[variant](copied, theme)

  if (customColor && !copied) {
    const baseObj = baseStyles as Record<string, unknown>
    const hoverStyles = baseObj['&:hover'] as Record<string, unknown> | undefined
    const colorOverride: SxProps<Theme> = {
      color: customColor,
      '&:hover': { ...hoverStyles, color: customColor },
    }
    return customSx
      ? { ...baseStyles, ...colorOverride, ...(customSx as object) }
      : { ...baseStyles, ...colorOverride }
  }

  return customSx ? { ...baseStyles, ...(customSx as object) } : baseStyles
}

function resolveIconSx(variant: CopyButtonVariant, customSize?: string | number): SxProps<Theme> {
  const baseStyles = variantIconStyles[variant]
  return customSize === undefined ? baseStyles : { ...baseStyles, fontSize: customSize }
}

const variantButtonStyles: Record<
  CopyButtonVariant,
  (copied: boolean, theme: Theme) => SxProps<Theme>
> = {
  default: (copied) => ({
    color: copied ? 'success.main' : 'inherit',
  }),
  small: (copied) => ({
    padding: '2px',
    color: copied ? 'success.main' : 'text.secondary',
    '&:hover': {
      backgroundColor: 'transparent',
      color: copied ? 'success.main' : 'primary.main',
    },
  }),
  overlay: (copied) => ({
    padding: '2px',
    color: copied ? 'success.main' : 'text.secondary',
    '&:hover': {
      backgroundColor: 'transparent',
      color: copied ? 'success.main' : 'primary.main',
    },
  }),
  controls: (copied) => ({
    color: copied ? 'success.main' : 'text.primary',
    '&:hover': {
      backgroundColor: 'transparent',
      color: copied ? 'success.main' : 'text.secondary',
    },
  }),
}

const variantIconStyles: Record<CopyButtonVariant, SxProps<Theme>> = {
  default: {
    fontSize: 'small',
  },
  small: {
    fontSize: '0.875rem',
  },
  overlay: {
    fontSize: '0.875rem',
  },
  controls: {
    fontSize: '1rem',
  },
}
